// app/learn/path/[slug]/study-plan.tsx
// =============================================================================
// Kursi Learn — Personalized Study Plan Screen
// =============================================================================

import { useCallback, useEffect, useState } from 'react';
import {
  ActivityIndicator,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { router, useLocalSearchParams } from 'expo-router';
import { theme } from '@/constants/theme';
import { LEARN_LAUNCH_CONFIG } from '@/constants/learnLaunchConfig';
import { RuntimeAsyncState } from '@/features/unified-learning/runtime/components/RuntimeAsyncState';

// ── Design tokens ─────────────────────────────────────────────────────────────

const BG       = '#0B0B14';
const SURFACE  = '#16142A';
const SURFACE2 = '#221F3D';
const SURFACE3 = '#2D2B50';
const TEXT     = '#F0EFF8';
const TEXT_MED = '#9896B4';
const TEXT_DIM = '#5E5C78';
const PRIMARY  = theme.colors.primary;
const GREEN    = '#10B981';
const AMBER    = '#F59E0B';
const RED      = '#EF4444';

const SUPABASE_URL      = process.env.EXPO_PUBLIC_SUPABASE_URL      ?? '';
const SUPABASE_ANON_KEY = process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY ?? '';

// ── Types ─────────────────────────────────────────────────────────────────────

type DailyTask = {
  id: string;
  task_type: string;
  title: string;
  description: string | null;
  target_count: number;
  done_count: number;
  completed: boolean;
  xp_reward: number;
};

type StudyPlan = {
  id: string;
  goal: string | null;
  exam_date: string | null;
  daily_minutes: number;
  days_per_week: number;
  current_phase: string;
  weak_topics: string[];
};

type Progress = {
  total_xp: number;
  streak_days: number;
  completed_lessons: number;
  total_lessons: number;
  recent_accuracy: number | null;
};

const TASK_ICONS: Record<string, string> = {
  lesson:          '▷',
  flashcards:      '◧',
  practice:        '◎',
  exam:            '▣',
  review_mistakes: '↺',
};

const PHASE_CONFIG: Record<string, { label: string; color: string; icon: string }> = {
  learning:  { label: 'למידה',        color: PRIMARY, icon: '▷' },
  review:    { label: 'חזרה',         color: AMBER,   icon: '↺' },
  exam_prep: { label: 'הכנה למבחן',  color: GREEN,   icon: '◎' },
  completed: { label: 'הושלם',        color: GREEN,   icon: '✓' },
};

// ── Main component ─────────────────────────────────────────────────────────────

export default function StudyPlanScreen() {
  const params  = useLocalSearchParams<{ slug: string; uid: string; worldId?: string }>();
  const slug    = typeof params.slug    === 'string' ? params.slug    : '';
  const userId  = typeof params.uid     === 'string' ? params.uid     : '';
  const worldId = typeof params.worldId === 'string' ? params.worldId : '';

  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);
  const [plan, setPlan]             = useState<StudyPlan | null>(null);
  const [tasks, setTasks]           = useState<DailyTask[]>([]);
  const [progress, setProgress]     = useState<Progress | null>(null);
  const [editing, setEditing]       = useState(false);
  const [editGoal, setEditGoal]     = useState('');
  const [editDate, setEditDate]     = useState('');
  const [editMinutes, setEditMinutes] = useState('20');

  const loadPlan = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/kp-study-plan`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${SUPABASE_ANON_KEY}`, 'apikey': SUPABASE_ANON_KEY },
        body:    JSON.stringify({ action: 'get', world_id: worldId, user_id: userId }),
      });
      const data = await res.json();
      setPlan(data.plan ?? null);
      setTasks(data.tasks_today ?? []);
      setProgress(data.progress ?? null);
      if (data.plan) {
        setEditGoal(data.plan.goal ?? '');
        setEditDate(data.plan.exam_date ?? '');
        setEditMinutes(String(data.plan.daily_minutes ?? 20));
      }
    } finally {
      setLoading(false);
    }
  }, [worldId, userId]);

  useEffect(() => { if (worldId && userId) loadPlan(); }, [worldId, userId, loadPlan]);

  const savePlan = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/kp-study-plan`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${SUPABASE_ANON_KEY}`, 'apikey': SUPABASE_ANON_KEY },
        body:    JSON.stringify({
          action:         'create',
          world_id:       worldId,
          user_id:        userId,
          goal:           editGoal,
          exam_date:      editDate || null,
          daily_minutes:  Number(editMinutes) || 20,
        }),
      });
      const data = await res.json();
      setPlan(data.plan ?? null);
      setTasks(data.tasks_today ?? []);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const completeTask = async (taskId: string) => {
    try {
      await fetch(`${SUPABASE_URL}/functions/v1/kp-study-plan`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${SUPABASE_ANON_KEY}`, 'apikey': SUPABASE_ANON_KEY },
        body:    JSON.stringify({ action: 'complete_task', task_id: taskId, user_id: userId }),
      });
      setTasks((prev) =>
        prev.map((t) => t.id === taskId ? { ...t, completed: true, done_count: t.target_count } : t),
      );
    } catch { /* non-fatal */ }
  };

  if (!LEARN_LAUNCH_CONFIG.advancedTools.studyPlan) {
    return (
      <RuntimeAsyncState
        state="degraded"
        title="תוכנית למידה זמינה בהמשך"
        description="בטא זו מתמקדת כרגע בלולאת הליבה: שיעור → תרגול → סקירה → מבחן. נפתח את הכלי מחדש אחרי חיזוק אמינות ותמיכה."
        actionLabel="חזרה לבית המסלול"
        onAction={() => {
          if (router.canGoBack()) router.back();
          else router.replace(slug ? `/learn/path/${slug}/home` as any : '/learn' as any);
        }}
      />
    );
  }

  if (loading) {
    return (
      <View style={s.centered}>
        <ActivityIndicator color={PRIMARY} size="large" />
        <Text style={s.loadingText}>טוען תוכנית...</Text>
      </View>
    );
  }

  const phaseConf = PHASE_CONFIG[plan?.current_phase ?? 'learning'] ?? PHASE_CONFIG.learning;
  const todayDone = tasks.filter((t) => t.completed).length;
  const todayTotal = tasks.length;
  const todayPct = todayTotal > 0 ? Math.round((todayDone / todayTotal) * 100) : 0;
  const completedLessons = progress?.completed_lessons ?? 0;
  const totalLessons = progress?.total_lessons ?? 0;
  const coursePct = totalLessons > 0 ? Math.round((completedLessons / totalLessons) * 100) : 0;

  return (
    <View style={s.root}>
      {/* Header */}
      <View style={s.header}>
        <Pressable onPress={() => router.back()} style={s.backBtn}>
          <Text style={s.backText}>‹ חזרה</Text>
        </Pressable>
        <Text style={s.headerTitle}>תוכנית למידה</Text>
        <Pressable style={s.editBtn} onPress={() => setEditing((v) => !v)}>
          <Text style={s.editBtnText}>{editing ? 'בטל' : '✏️ ערוך'}</Text>
        </Pressable>
      </View>

      <ScrollView style={{ flex: 1 }} contentContainerStyle={s.content} showsVerticalScrollIndicator={false}>

        {/* Edit form */}
        {editing && (
          <View style={s.editCard}>
            <Text style={s.editTitle}>הגדרות תוכנית</Text>
            <View style={s.field}>
              <Text style={s.fieldLabel}>מטרת הלמידה</Text>
              <TextInput
                style={s.fieldInput}
                value={editGoal}
                onChangeText={setEditGoal}
                placeholder="לדוגמה: להכין לבחינת הסמכה"
                placeholderTextColor={TEXT_DIM}
                textAlign="right"
              />
            </View>
            <View style={s.field}>
              <Text style={s.fieldLabel}>תאריך מבחן / יעד (YYYY-MM-DD)</Text>
              <TextInput
                style={s.fieldInput}
                value={editDate}
                onChangeText={setEditDate}
                placeholder="2026-06-01"
                placeholderTextColor={TEXT_DIM}
                textAlign="right"
              />
            </View>
            <View style={s.field}>
              <Text style={s.fieldLabel}>דקות ללמידה ביום</Text>
              <View style={s.minuteRow}>
                {[10, 20, 30, 45, 60].map((m) => (
                  <Pressable
                    key={m}
                    style={[s.minuteChip, editMinutes === String(m) && { backgroundColor: PRIMARY, borderColor: PRIMARY }]}
                    onPress={() => setEditMinutes(String(m))}
                  >
                    <Text style={[s.minuteChipText, editMinutes === String(m) && { color: '#fff' }]}>{m}′</Text>
                  </Pressable>
                ))}
              </View>
            </View>
            <Pressable style={[s.saveBtn, saving && { opacity: 0.7 }]} onPress={savePlan} disabled={saving}>
              {saving
                ? <ActivityIndicator size="small" color="#fff" />
                : <Text style={s.saveBtnText}>שמור תוכנית</Text>
              }
            </Pressable>
          </View>
        )}

        {/* Phase indicator */}
        <View style={[s.phaseCard, { borderColor: phaseConf.color + '33' }]}>
          <Text style={s.phaseIcon}>{phaseConf.icon}</Text>
          <View style={s.phaseText}>
            <Text style={s.phaseSup}>שלב נוכחי</Text>
            <Text style={[s.phaseLabel, { color: phaseConf.color }]}>{phaseConf.label}</Text>
          </View>
          {plan?.goal && (
            <View style={s.goalTag}>
              <Text style={s.goalTagText} numberOfLines={1}>{plan.goal}</Text>
            </View>
          )}
        </View>

        {/* Progress stats */}
        <View style={s.statsRow}>
          <StatBox icon="▷" value={`${completedLessons}/${totalLessons}`} label="שיעורים" color={PRIMARY} />
          <StatBox icon="★" value={String(progress?.total_xp ?? 0)} label="נק׳" color={AMBER} />
          <StatBox icon="▲" value={String(progress?.streak_days ?? 0)} label="ימי רצף" color={RED} />
          {progress?.recent_accuracy !== null && (
            <StatBox icon="◎" value={`${progress?.recent_accuracy}%`} label="דיוק" color={GREEN} />
          )}
        </View>

        {/* Course progress bar */}
        <View style={s.courseProgressCard}>
          <View style={s.cpHeader}>
            <Text style={s.cpTitle}>התקדמות בקורס</Text>
            <Text style={[s.cpPct, { color: PRIMARY }]}>{coursePct}%</Text>
          </View>
          <View style={s.cpTrack}>
            <View style={[s.cpFill, { width: `${coursePct}%` as any, backgroundColor: PRIMARY }]} />
          </View>
        </View>

        {/* Today's tasks */}
        <View style={s.sectionRow}>
          <Text style={s.sectionTitle}>משימות היום</Text>
          <View style={s.todayBadge}>
            <Text style={s.todayBadgeText}>{todayDone}/{todayTotal}</Text>
          </View>
        </View>

        {/* Day progress */}
        <View style={s.dayTrack}>
          <View style={[s.dayFill, { width: `${todayPct}%` as any, backgroundColor: todayPct === 100 ? GREEN : PRIMARY }]} />
        </View>

        {tasks.map((task) => (
          <TaskRow
            key={task.id}
            task={task}
            onComplete={() => completeTask(task.id)}
          />
        ))}

        {/* Exam date countdown */}
        {plan?.exam_date && (
          <ExamCountdown examDate={plan.exam_date} />
        )}

        <View style={{ height: 32 }} />
      </ScrollView>
    </View>
  );
}

// ── Task row ──────────────────────────────────────────────────────────────────

function TaskRow({ task, onComplete }: { task: DailyTask; onComplete: () => void }) {
  const icon = TASK_ICONS[task.task_type] ?? '📌';
  return (
    <Pressable
      style={[tr.row, task.completed && tr.rowDone]}
      onPress={task.completed ? undefined : onComplete}
      accessibilityRole="button"
      disabled={task.completed}
    >
      <View style={[tr.check, task.completed && { backgroundColor: GREEN, borderColor: GREEN }]}>
        {task.completed && <Text style={tr.checkMark}>✓</Text>}
      </View>
      <View style={tr.body}>
        <View style={tr.titleRow}>
          <Text style={tr.icon}>{icon}</Text>
          <Text style={[tr.title, task.completed && tr.titleDone]} numberOfLines={1}>
            {task.title}
          </Text>
        </View>
        {task.description && (
          <Text style={tr.desc} numberOfLines={1}>{task.description}</Text>
        )}
        <View style={tr.meta}>
          {task.target_count > 1 && (
            <Text style={tr.metaItem}>{task.done_count}/{task.target_count}</Text>
          )}
          <Text style={[tr.metaItem, { color: AMBER }]}>+{task.xp_reward} XP</Text>
        </View>
      </View>
    </Pressable>
  );
}

const tr = StyleSheet.create({
  row: {
    backgroundColor: SURFACE, borderRadius: 14, borderWidth: 1, borderColor: SURFACE3,
    padding: 14, flexDirection: 'row-reverse', alignItems: 'center', gap: 12,
  },
  rowDone: { opacity: 0.55 },
  check: {
    width: 24, height: 24, borderRadius: 12, borderWidth: 2,
    borderColor: SURFACE3, alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  },
  checkMark: { color: '#fff', fontFamily: theme.fonts.bold, fontSize: 12 },
  body: { flex: 1, gap: 3 },
  titleRow: { flexDirection: 'row-reverse', alignItems: 'center', gap: 6 },
  icon: { fontSize: 15 },
  title: { fontFamily: theme.fonts.bold, fontSize: 14, color: TEXT, flex: 1, textAlign: 'right' },
  titleDone: { textDecorationLine: 'line-through' as any, opacity: 0.7 },
  desc: { fontFamily: theme.fonts.regular, fontSize: 11, color: TEXT_MED, textAlign: 'right' },
  meta: { flexDirection: 'row-reverse', gap: 8, marginTop: 2 },
  metaItem: { fontFamily: theme.fonts.medium, fontSize: 11, color: TEXT_DIM },
});

// ── Exam countdown ─────────────────────────────────────────────────────────────

function ExamCountdown({ examDate }: { examDate: string }) {
  const days = Math.ceil((new Date(examDate).getTime() - Date.now()) / 86400000);
  if (days < 0) return null;
  const color = days <= 3 ? RED : days <= 7 ? AMBER : GREEN;

  return (
    <View style={[ec.card, { borderColor: color + '33' }]}>
      <Text style={ec.icon}>◷</Text>
      <View style={ec.text}>
        <Text style={ec.title}>יעד: {new Date(examDate).toLocaleDateString('he-IL')}</Text>
        <Text style={[ec.days, { color }]}>
          {days === 0 ? 'היום!' : `${days} ימים`}
        </Text>
      </View>
    </View>
  );
}

const ec = StyleSheet.create({
  card: {
    backgroundColor: SURFACE, borderRadius: 14, borderWidth: 1,
    padding: 14, flexDirection: 'row-reverse', alignItems: 'center', gap: 12,
  },
  icon: { fontSize: 24 },
  text: { flex: 1, gap: 2 },
  title: { fontFamily: theme.fonts.regular, fontSize: 13, color: TEXT_MED, textAlign: 'right' },
  days: { fontFamily: theme.fonts.bold, fontSize: 16, textAlign: 'right' },
});

// ── Stat box ──────────────────────────────────────────────────────────────────

function StatBox({ icon, value, label, color }: { icon: string; value: string; label: string; color: string }) {
  return (
    <View style={[sb.box, { borderColor: color + '22' }]}>
      <Text style={sb.icon}>{icon}</Text>
      <Text style={[sb.value, { color }]}>{value}</Text>
      <Text style={sb.label}>{label}</Text>
    </View>
  );
}

const sb = StyleSheet.create({
  box: {
    flex: 1, backgroundColor: SURFACE, borderRadius: 12, borderWidth: 1,
    padding: 10, alignItems: 'center', gap: 3,
  },
  icon:  { fontSize: 16 },
  value: { fontFamily: theme.fonts.bold, fontSize: 16 },
  label: { fontFamily: theme.fonts.regular, fontSize: 10, color: TEXT_DIM, textAlign: 'center' },
});

// ── Styles ─────────────────────────────────────────────────────────────────────

const s = StyleSheet.create({
  root:    { flex: 1, backgroundColor: BG },
  centered: {
    flex: 1, backgroundColor: BG,
    alignItems: 'center', justifyContent: 'center', gap: 14,
  },
  loadingText: { color: TEXT_MED, fontFamily: theme.fonts.regular, fontSize: 14 },

  header: {
    flexDirection: 'row-reverse', alignItems: 'center',
    paddingHorizontal: 16,
    paddingTop: Platform.OS === 'web' ? 20 : 16,
    paddingBottom: 14,
    borderBottomWidth: 1, borderBottomColor: SURFACE3,
  },
  backBtn:     { paddingHorizontal: 8, paddingVertical: 6, minWidth: 64 },
  backText:    { color: PRIMARY, fontFamily: theme.fonts.medium, fontSize: 15 },
  headerTitle: { flex: 1, color: TEXT, fontFamily: theme.fonts.bold, fontSize: 17, textAlign: 'center' },
  editBtn:     { paddingHorizontal: 8, paddingVertical: 6, minWidth: 64, alignItems: 'flex-end' },
  editBtnText: { color: PRIMARY, fontFamily: theme.fonts.medium, fontSize: 13 },

  content: {
    paddingHorizontal: 16, paddingTop: 18, paddingBottom: 20,
    maxWidth: 600, alignSelf: 'center' as any, width: '100%', gap: 12,
  },

  // Edit card
  editCard: {
    backgroundColor: SURFACE, borderRadius: 18, borderWidth: 1, borderColor: PRIMARY + '22',
    padding: 18, gap: 14,
  },
  editTitle: { fontFamily: theme.fonts.bold, fontSize: 16, color: TEXT, textAlign: 'right' },
  field: { gap: 6 },
  fieldLabel: { fontFamily: theme.fonts.medium, fontSize: 12, color: TEXT_MED, textAlign: 'right' },
  fieldInput: {
    backgroundColor: SURFACE2, borderWidth: 1, borderColor: SURFACE3,
    borderRadius: 10, paddingHorizontal: 14, paddingVertical: 10,
    fontFamily: theme.fonts.regular, fontSize: 14, color: TEXT,
  },
  minuteRow: { flexDirection: 'row-reverse', gap: 8, flexWrap: 'wrap' as any },
  minuteChip: {
    borderRadius: 99, borderWidth: 1, borderColor: SURFACE3,
    paddingHorizontal: 14, paddingVertical: 7, backgroundColor: SURFACE2,
  },
  minuteChipText: { fontFamily: theme.fonts.bold, fontSize: 13, color: TEXT_MED },
  saveBtn: {
    backgroundColor: PRIMARY, borderRadius: 12, paddingVertical: 13,
    alignItems: 'center',
  },
  saveBtnText: { fontFamily: theme.fonts.bold, fontSize: 14, color: '#fff' },

  // Phase card
  phaseCard: {
    backgroundColor: SURFACE, borderRadius: 16, borderWidth: 1,
    padding: 16, flexDirection: 'row-reverse', alignItems: 'center', gap: 12,
  },
  phaseIcon: { fontSize: 28 },
  phaseText: { flex: 1, gap: 2 },
  phaseSup:  { fontFamily: theme.fonts.bold, fontSize: 10, color: TEXT_DIM, textAlign: 'right', letterSpacing: 0.4, textTransform: 'uppercase' as any },
  phaseLabel: { fontFamily: theme.fonts.bold, fontSize: 16, textAlign: 'right' },
  goalTag: {
    backgroundColor: SURFACE2, borderRadius: 99, paddingHorizontal: 10, paddingVertical: 4,
    maxWidth: 120,
  },
  goalTagText: { fontFamily: theme.fonts.regular, fontSize: 11, color: TEXT_MED, textAlign: 'center' },

  statsRow: { flexDirection: 'row-reverse', gap: 8 },

  // Course progress
  courseProgressCard: {
    backgroundColor: SURFACE, borderRadius: 16, borderWidth: 1, borderColor: SURFACE3,
    padding: 16, gap: 10,
  },
  cpHeader: { flexDirection: 'row-reverse', justifyContent: 'space-between', alignItems: 'center' },
  cpTitle:  { fontFamily: theme.fonts.bold, fontSize: 14, color: TEXT },
  cpPct:    { fontFamily: theme.fonts.bold, fontSize: 16 },
  cpTrack:  { height: 8, borderRadius: 4, backgroundColor: SURFACE3, overflow: 'hidden' },
  cpFill:   { height: 8, borderRadius: 4 },

  sectionRow: {
    flexDirection: 'row-reverse', alignItems: 'center', justifyContent: 'space-between',
    paddingTop: 4,
  },
  sectionTitle: {
    fontFamily: theme.fonts.bold, fontSize: 12, color: TEXT_DIM,
    textTransform: 'uppercase' as any, letterSpacing: 0.6,
  },
  todayBadge: {
    backgroundColor: PRIMARY + '1A', borderRadius: 99, borderWidth: 1, borderColor: PRIMARY + '33',
    paddingHorizontal: 10, paddingVertical: 3,
  },
  todayBadgeText: { fontFamily: theme.fonts.bold, fontSize: 12, color: PRIMARY },

  dayTrack: { height: 4, borderRadius: 2, backgroundColor: SURFACE3, overflow: 'hidden' },
  dayFill:  { height: 4, borderRadius: 2 },
});
